// Register.tsx
import React, { useState } from "react";
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from "react-router";
import InputAdornment from '@mui/material/InputAdornment';
import { Button, Grid2, Input } from '@mui/material';
import Email from '@mui/icons-material/Person';
import PinIcon from '@mui/icons-material/Pin';
import PasswordIcon from '@mui/icons-material/Password';
import SendIcon from '@mui/icons-material/Send';
import CancelIcon from '@mui/icons-material/Cancel';
import { NotificationType, useNotification } from '../../hooks/use-notification';
import SingleForm from "../SingleForm";
import { PasswordRules, passwordValidator } from "../../common/PasswordRules";

enum AuthStep {
  INITIAL = "INITIAL",
  CODE = "CODE",
}

const ForgotPassword: React.FC = () => {
  const { forgotPassword, forgotPasswordConfirm, authError, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<AuthStep>(AuthStep.INITIAL);
  const navigate = useNavigate();

  const { set: setNotification } = useNotification();

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    const result = await forgotPassword(email);
    if (!result || !result.nextStep) {
      return;
    }
    const { nextStep } = result;
    switch (nextStep.resetPasswordStep) {
      case "CONFIRM_RESET_PASSWORD_WITH_CODE":
        const codeDeliveryDetails = nextStep.codeDeliveryDetails;
        console.log(`Confirmation code was sent to registered ${codeDeliveryDetails.deliveryMedium}`);
        // Collect the confirmation code from the user and pass to confirmResetPassword.
        setStep(AuthStep.CODE);
        break;
      case "DONE":
        console.log("Successfully reset password.");
        break;
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!passwordValidator(password)) {
      setNotification({
        type: NotificationType.error,
        message: 'Invalid password'
      });
      return;
    }
    
    if (password !== confirmPassword) {
      setNotification({
        type: NotificationType.error,
        message: 'Password do not match'
      });
      return;
    }

    const result = await forgotPasswordConfirm(email, code, password);
    if (result) {
      console.log("User confirmed successfully:", result);
      // Redirect or update UI as needed.
      navigate("/");
    }
  };

  return (
    <SingleForm
      title={step !== AuthStep.CODE ? "Forgot Password" : "Reset Password"}
      onSubmit={step === AuthStep.INITIAL ? handleForgotPassword : handleResetPassword}
    >
      {step === AuthStep.INITIAL && (
        <>
          <Input
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            fullWidth
            placeholder="Email"
            startAdornment={
              <InputAdornment position="start">
                <Email color="primary" />
              </InputAdornment>
            }
          />
          {authError && <p style={{ color: "red", whiteSpace: 'pre-line' }}>{authError}</p>}
          <Grid2 container spacing={4} sx={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}>
            <Grid2 size={5}>
              <Button
                fullWidth
                type="submit"
                variant="contained"
                color="primary"
                startIcon={<SendIcon />}
              >
                {loading ? "Submitting..." : "Forgot Password"}
              </Button>
            </Grid2>
            <Grid2 size={5}>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<CancelIcon />}
                onClick={() => navigate("/")}
              >
                Back to Login
              </Button>
            </Grid2>
          </Grid2>

          <a href="#" onClick={() => setStep(AuthStep.CODE)}>I already have a code</a>

        </>
      )}
      {step === AuthStep.CODE && (
        <>
          <Input
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            fullWidth
            placeholder="Email"
            startAdornment={
              <InputAdornment position="start">
                <Email color="primary" />
              </InputAdornment>
            }
          />
          <Input
            value={code}
            onChange={e => setCode(e.target.value)}
            required
            placeholder="Verification Code"
            type="text"
            startAdornment={
              <InputAdornment position="start">
                <PinIcon color="primary" />
              </InputAdornment>
            }
          />
          <Input
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Enter New Password"
            type="password"
            startAdornment={
              <InputAdornment position="start">
                <PasswordIcon color="primary" />
              </InputAdornment>
            }
          />
          <PasswordRules password={password} />
          <Input
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            placeholder="Enter New Password Again"
            type="password"
            startAdornment={
              <InputAdornment position="start">
                <PasswordIcon color="primary" />
              </InputAdornment>
            }
          />
          {authError && <p style={{ color: "red", whiteSpace: 'pre-line' }}>{authError}</p>}
          <Button
            fullWidth
            type="submit"
            variant="contained"
            color="primary"
            sx={{ width: "50%", my: 2 }}
            startIcon={<SendIcon />}
          >
            {loading ? "Resetting..." : "Reset Password"}
          </Button>
          <a href="#" onClick={() => setStep(AuthStep.INITIAL)}>Back to forgot password for new code</a>
        </>
      )}
    </SingleForm>
  );
};

export default ForgotPassword;
